import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Upload, Save, Palette, Shield, Radar, MessageCircle, CheckCircle2, XCircle, Loader2, Info, Clock, Database } from "lucide-react";
import { hasPerm, isSuperAdmin } from "@/lib/roles";
import { PartidaCard } from "@/components/PartidaCard";
import { useAuth } from "@/context/AuthContext";

// Página de configuração da PRÓPRIA empresa (admin/gerente da unidade).
// Diferente de /admin/empresas (super admin, gerencia TODAS): aqui edita só a atual.
// Slug (URL) NÃO é editável pra não quebrar bookmarks/subdomínios em uso.

const formatCep = (s) => (s || "").replace(/\D/g, "").slice(0, 8).replace(/^(\d{5})(\d)/, "$1-$2");
const formatCnpj = (s) => {
  const d = (s || "").replace(/\D/g, "").slice(0, 14);
  return d.replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};
async function lookupCep(cep) {
  const digits = (cep || "").replace(/\D/g, "");
  if (digits.length !== 8) return null;
  try {
    const r = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    const d = await r.json();
    return d.erro ? null : d;
  } catch { return null; }
}

// Destaca URLs em azul (visualmente parecido com o WhatsApp que auto-linkifica).
// Retorna um array de nodes React — usado no preview do template Z-API.
// Key composta (posição + fragmento do conteúdo) evita bugs de reconciliação
// quando o texto do preview muda em tempo real (fev/2026 code-quality).
function renderPreviewWithLinks(text) {
  const parts = String(text || "").split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) => {
    const k = `${i}:${part.slice(0, 24)}`;
    return /^https?:\/\//.test(part)
      ? <span key={k} className="text-sky-600 underline break-all">{part}</span>
      : <span key={k}>{part}</span>;
  });
}

export default function EmpresaConfig() {
  const { user } = useAuth();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const logoRef = useRef(null);
  // Matrix-first: permissão de ALTERAR a empresa vem da matrix (empresas.a=true).
  // Super Admin sempre pode. Sem matrix populada = read-only.
  const canEdit = isSuperAdmin(user) || hasPerm(user, "empresas", "a");
  // Iter 77 (fev/2026): permissões separadas pra o card Z-API.
  // canViewZapi controla se o card inteiro aparece; canEditZapi permite edição.
  const canViewZapi = isSuperAdmin(user) || hasPerm(user, "whatsapp_config", "v");
  const canEditZapi = isSuperAdmin(user) || hasPerm(user, "whatsapp_config", "a");

  useEffect(() => {
    api.get("/empresa").then((r) => setF(r.data || {}))
      .catch((e) => toast.error(e?.response?.data?.detail || "Erro ao carregar empresa"));
  }, []);

  // Estado do teste Z-API (fev/2026)
  const [zapiTest, setZapiTest] = useState(null); // {loading, result}
  const [zapiTestPhone, setZapiTestPhone] = useState("");
  const [templateVars, setTemplateVars] = useState({ vars: [], default_template: "" });
  useEffect(() => {
    api.get("/whatsapp/template-vars").then((r) => setTemplateVars(r.data)).catch(() => {});
  }, []);

  // Iter 80 (fev/2026) — Preview do template em tempo real.
  // Debounce 400ms + chama /whatsapp/preview → usa a MESMA render_template
  // do envio real (garante paridade com o que o cliente vai receber).
  const [preview, setPreview] = useState("");
  useEffect(() => {
    if (!canViewZapi) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/whatsapp/preview", {
          template: f?.whatsapp_template || "",
        });
        setPreview(r.data.preview || "");
      } catch { /* silencioso — evita ruído se o admin ainda tá digitando */ }
    }, 400);
    return () => clearTimeout(t);
  }, [f?.whatsapp_template, canViewZapi]);

  const runZapiTest = async () => {
    if (!f?.id) return;
    setZapiTest({ loading: true });
    try {
      const body = zapiTestPhone ? { test_phone: zapiTestPhone } : {};
      const r = await api.post(`/empresas/${f.id}/zapi/test`, body);
      setZapiTest({ loading: false, result: r.data });
      const conn = r.data?.connection || {};
      if (conn.ok) toast.success("Z-API conectada e smartphone online.");
      else toast.error(`Falha: ${conn.error || "verifique credenciais"}`);
    } catch (e) {
      setZapiTest({ loading: false, result: { connection: { ok: false, error: e?.response?.data?.detail || "Erro" } } });
      toast.error("Erro ao testar Z-API");
    }
  };

  const update = (patch) => setF((prev) => ({ ...prev, ...patch }));

  const onCepBlur = async () => {
    if (!f?.cep) return;
    setCepLoading(true);
    const d = await lookupCep(f.cep);
    setCepLoading(false);
    if (!d) return;
    const parts = [d.logradouro, d.bairro, d.localidade, d.uf].filter(Boolean).join(", ");
    update({ endereco: parts, ...(d.localidade && !f.cidade ? { cidade: d.localidade } : {}), ...(d.uf && !f.uf ? { uf: d.uf } : {}) });
    toast.success("Endereço preenchido pelo CEP");
  };

  const onLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) return toast.error("Logo maior que 500KB. Reduza a imagem.");
    const reader = new FileReader();
    reader.onload = () => update({ logo_base64: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Slug NÃO é enviado — imutável pela UI para não quebrar URLs em uso.
      // Ponto de partida é salvo pelo próprio card (POST /empresa/partida).
      const { slug, id, _id, created_at, updated_at, partida_endereco, partida_lat, partida_lng, ...payload } = f;
      await api.put("/empresa", payload);
      // Dispara evento para o header/sidebar recarregarem branding
      window.dispatchEvent(new Event("empresa-changed"));
      toast.success("Dados da empresa atualizados");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  if (!f) return <div className="min-h-[300px] flex items-center justify-center text-muted-foreground">Carregando…</div>;

  return (
    <div className="space-y-6" data-testid="page-empresa-config">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-orange-600 text-white"><Building2 className="w-6 h-6" /></div>
        <div>
          <h1 className="text-3xl font-extrabold leading-tight">Configuração da Empresa</h1>
          <p className="text-sm text-muted-foreground">Atualize logo, endereço, telefone e ajustes operacionais da sua unidade.</p>
        </div>
      </div>

      {!canEdit && (
        <div className="p-3 bg-amber-50 border border-amber-300 rounded text-sm text-amber-800" data-testid="config-readonly-banner">
          Você tem acesso somente-leitura. Para editar, peça ao administrador a permissão <b>Gerenciar Configurações da Empresa</b> (Matriz → empresas → Alterar).
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4"/>Identificação</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-3">
          <div><Label>Nome da empresa</Label>
            <Input value={f.nome || ""} onChange={(e) => update({ nome: e.target.value })} disabled={!canEdit} data-testid="input-nome" /></div>
          <div><Label>URL da empresa <span className="text-xs text-muted-foreground">(travado)</span></Label>
            <Input value={f.slug ? `usegestao.app.br/${f.slug}` : "—"} disabled data-testid="input-slug" /></div>
          <div><Label>CNPJ</Label>
            <Input value={f.cnpj || ""} onChange={(e) => update({ cnpj: formatCnpj(e.target.value) })} disabled={!canEdit} data-testid="input-cnpj" placeholder="00.000.000/0000-00" /></div>
          <div><Label>Inscrição Estadual</Label>
            <Input value={f.inscricao_estadual || ""} onChange={(e) => update({ inscricao_estadual: e.target.value })} disabled={!canEdit} data-testid="input-ie" /></div>
          <div><Label>Segmento</Label>
            <Input value={f.segmento || ""} onChange={(e) => update({ segmento: e.target.value })} disabled={!canEdit} data-testid="input-segmento" placeholder="Farmácia" /></div>
          <div><Label>Email de contato</Label>
            <Input type="email" value={f.email || ""} onChange={(e) => update({ email: e.target.value })} disabled={!canEdit} data-testid="input-email" /></div>
          <div className="md:col-span-2"><Label>Slogan / tagline</Label>
            <Input value={f.tagline || ""} onChange={(e) => update({ tagline: e.target.value })} disabled={!canEdit} data-testid="input-tagline" placeholder="Gestão de entregas para farmácias" /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Endereço</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-3">
          <div><Label>CEP {cepLoading && <span className="text-xs text-muted-foreground">(consultando…)</span>}</Label>
            <Input value={f.cep || ""} onChange={(e) => update({ cep: formatCep(e.target.value) })} onBlur={onCepBlur} disabled={!canEdit} data-testid="input-cep" placeholder="00000-000" /></div>
          <div className="md:col-span-2"><Label>Endereço completo</Label>
            <Input value={f.endereco || ""} onChange={(e) => update({ endereco: e.target.value })} disabled={!canEdit} data-testid="input-endereco" placeholder="Rua, número, bairro, cidade" /></div>
          <div className="md:col-span-2"><Label>Cidade <span className="text-xs text-muted-foreground">(usada para localizar os bairros no mapa de rotas)</span></Label>
            <Input value={f.cidade || ""} onChange={(e) => update({ cidade: e.target.value })} disabled={!canEdit} data-testid="input-cidade" placeholder="Ex.: São Paulo" /></div>
          <div><Label>UF</Label>
            <Input value={f.uf || ""} onChange={(e) => update({ uf: e.target.value.toUpperCase().slice(0, 2) })} disabled={!canEdit} data-testid="input-uf" placeholder="SP" maxLength={2} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Contato / WhatsApp</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-3">
          <div><Label>Telefone principal</Label>
            <Input value={f.telefone || ""} onChange={(e) => update({ telefone: e.target.value })} disabled={!canEdit} data-testid="input-telefone" placeholder="(00) 0000-0000" /></div>
          <div><Label>WhatsApp 1</Label>
            <Input value={f.whatsapp1 || ""} onChange={(e) => update({ whatsapp1: e.target.value })} disabled={!canEdit} data-testid="input-wpp1" placeholder="(00) 00000-0000" /></div>
          <div><Label>WhatsApp 2 (opcional)</Label>
            <Input value={f.whatsapp2 || ""} onChange={(e) => update({ whatsapp2: e.target.value })} disabled={!canEdit} data-testid="input-wpp2" /></div>
          <div><Label>Template da mensagem <span className="text-xs text-muted-foreground">(vars: {"{cliente}, {hora}, {pedido}"})</span></Label>
            <Textarea value={f.whatsapp_msg_template || ""} onChange={(e) => update({ whatsapp_msg_template: e.target.value })} disabled={!canEdit} data-testid="input-wpp-template" rows={2} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Palette className="w-4 h-4"/>Identidade Visual</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4 items-end">
          <div>
            <Label>Cor primária (branding)</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={f.cor_primaria || "#EA580C"} onChange={(e) => update({ cor_primaria: e.target.value })} disabled={!canEdit}
                className="w-14 h-10 border rounded cursor-pointer" data-testid="input-cor" />
              <Input value={f.cor_primaria || "#EA580C"} onChange={(e) => update({ cor_primaria: e.target.value })} disabled={!canEdit} className="w-32" />
            </div>
          </div>
          <div>
            <Label>Logo</Label>
            <div className="flex items-center gap-3 mt-1">
              {f.logo_base64 ? (
                <img src={f.logo_base64} alt="logo" className="w-16 h-16 rounded border object-contain bg-white" />
              ) : (
                <div className="w-16 h-16 rounded border flex items-center justify-center text-muted-foreground bg-orange-50"><Building2 className="w-6 h-6" /></div>
              )}
              <input ref={logoRef} type="file" accept="image/*" hidden onChange={onLogoUpload} />
              <Button variant="outline" onClick={() => logoRef.current?.click()} disabled={!canEdit} data-testid="btn-upload-logo">
                <Upload className="w-4 h-4 mr-1" />Enviar logo
              </Button>
              {f.logo_base64 && canEdit && (
                <Button variant="ghost" size="sm" onClick={() => update({ logo_base64: "" })}>Remover</Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Ideal PNG transparente até 500KB.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4"/>Validação de Entregas</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <label className="flex items-start gap-3 p-2 rounded border bg-orange-50/30 cursor-pointer">
            <input type="checkbox" checked={!!f.require_foto_evidencia} onChange={(e) => update({ require_foto_evidencia: e.target.checked })} disabled={!canEdit}
              className="mt-1 accent-orange-600" data-testid="chk-req-foto" />
            <div>
              <div className="font-semibold text-sm">Exigir foto na conclusão</div>
              <div className="text-xs text-muted-foreground">O entregador precisa tirar uma foto no ato da entrega/cancelamento.</div>
            </div>
          </label>
          <label className="flex items-start gap-3 p-2 rounded border bg-orange-50/30 cursor-pointer">
            <input type="checkbox" checked={!!f.require_codigo_4dig} onChange={(e) => update({ require_codigo_4dig: e.target.checked })} disabled={!canEdit}
              className="mt-1 accent-orange-600" data-testid="chk-req-codigo" />
            <div>
              <div className="font-semibold text-sm">Confirmação por 4 dígitos do telefone</div>
              <div className="text-xs text-muted-foreground">O entregador digita os 4 últimos números do celular do cliente pra confirmar a entrega.</div>
            </div>
          </label>
          <label className="flex items-start gap-3 p-2 rounded border bg-orange-50/30 cursor-pointer">
            <input type="checkbox" checked={!!f.require_gps_capture} onChange={(e) => update({ require_gps_capture: e.target.checked })} disabled={!canEdit}
              className="mt-1 accent-orange-600" data-testid="chk-req-gps" />
            <div>
              <div className="font-semibold text-sm">Exigir GPS ativo na conclusão</div>
              <div className="text-xs text-muted-foreground">Bloqueia a entrega se o celular do entregador estiver com GPS desligado.</div>
            </div>
          </label>
        </CardContent>
      </Card>

      {canViewZapi && (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-600"/>Integração WhatsApp (Z-API)
            {f.zapi_configurada && <span className="text-[10px] uppercase font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">configurada</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-xs text-muted-foreground bg-slate-50 border rounded p-2 flex gap-2">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
            <div>
              Configure sua instância <a href="https://z-api.io" target="_blank" rel="noreferrer" className="underline">Z-API</a> pra enviar mensagens automáticas de rastreamento ao cliente.
              Os tokens são armazenados criptografados e <b>nunca aparecem em texto puro</b> no painel depois de salvos.
            </div>
          </div>

          <label className="flex items-start gap-3 p-2 rounded border bg-amber-50/50 border-amber-200 cursor-pointer">
            <input type="checkbox" checked={!!f.zapi_mock_mode} onChange={(e) => update({ zapi_mock_mode: e.target.checked })} disabled={!canEditZapi}
              className="mt-1 accent-amber-600" data-testid="chk-zapi-mock" />
            <div>
              <div className="font-semibold text-sm">🧪 Modo Mock (homologação / testes)</div>
              <div className="text-xs text-muted-foreground">
                Quando <b>ligado</b>, o sistema simula a Z-API sem chamar a API real (ideal pra QA e demo).
                Números terminando em dígito par = "tem WhatsApp"; ímpar = "sem WhatsApp".
                Desligue em produção pra usar as credenciais reais abaixo.
              </div>
            </div>
          </label>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Instance ID</Label>
              <Input value={f.zapi_instance_id || ""} onChange={(e) => update({ zapi_instance_id: e.target.value })} disabled={!canEditZapi} data-testid="input-zapi-instance" placeholder="3E6F..." /></div>
            <div><Label>Token da Instância {f.zapi_token_preview && <span className="text-[10px] text-emerald-700 ml-2">atual: {f.zapi_token_preview}</span>}</Label>
              <Input type="password" value={f.zapi_token || ""} onChange={(e) => update({ zapi_token: e.target.value })} disabled={!canEditZapi} data-testid="input-zapi-token" placeholder={f.zapi_token_preview ? "Preencha só pra trocar" : "Cole aqui"} /></div>
            <div><Label>Client-Token {f.zapi_client_token_preview && <span className="text-[10px] text-emerald-700 ml-2">atual: {f.zapi_client_token_preview}</span>}</Label>
              <Input type="password" value={f.zapi_client_token || ""} onChange={(e) => update({ zapi_client_token: e.target.value })} disabled={!canEditZapi} data-testid="input-zapi-client-token" placeholder={f.zapi_client_token_preview ? "Preencha só pra trocar" : "Header Client-Token"} /></div>
            <div><Label>WhatsApp Oficial (Contato)</Label>
              <Input value={f.whatsapp_farmacia || ""} onChange={(e) => update({ whatsapp_farmacia: e.target.value })} disabled={!canEditZapi} data-testid="input-whatsapp-farmacia" placeholder="(11) 99999-9999" /></div>
          </div>

          <label className="flex items-start gap-3 p-2 rounded border bg-emerald-50/40">
            <input type="checkbox" checked={!!f.whatsapp_trigger_ativo} onChange={(e) => update({ whatsapp_trigger_ativo: e.target.checked })} disabled={!canEditZapi || !f.zapi_configurada}
              className="mt-1 accent-emerald-600" data-testid="chk-whatsapp-trigger" />
            <div>
              <div className="font-semibold text-sm">Gatilho automático — enviar quando entrar em "Em Trânsito"</div>
              <div className="text-xs text-muted-foreground">Quando <b>ligado</b>, o cliente recebe o link de rastreio no WhatsApp automaticamente. Precisa da Z-API configurada acima.</div>
            </div>
          </label>

          <div>
            <Label>Modelo de Mensagem <span className="text-xs text-muted-foreground">(deixe em branco pra usar o padrão)</span></Label>
            <Textarea
              value={f.whatsapp_template || ""}
              onChange={(e) => update({ whatsapp_template: e.target.value })}
              disabled={!canEditZapi}
              data-testid="textarea-whatsapp-template"
              rows={7}
              placeholder={templateVars.default_template}
              className="font-mono text-sm"
            />
            <div className="mt-1 flex flex-wrap gap-1">
              <span className="text-[10px] text-muted-foreground mr-1">Variáveis:</span>
              {templateVars.vars.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => canEditZapi && update({ whatsapp_template: (f.whatsapp_template || templateVars.default_template) + `{${v}}` })}
                  disabled={!canEditZapi}
                  className="text-[10px] font-mono bg-slate-100 hover:bg-emerald-100 border rounded px-1.5 py-0.5"
                  data-testid={`btn-var-${v}`}
                  title={`Clique pra inserir {${v}} no modelo`}
                >
                  {"{" + v + "}"}
                </button>
              ))}
            </div>
          </div>

          {/* Iter 80: Preview em tempo real de como a mensagem final vai chegar
              no WhatsApp do cliente. Usa a mesma render_template do envio real. */}
          <div className="border rounded-lg overflow-hidden" data-testid="whatsapp-preview">
            <div className="bg-slate-100 px-3 py-1.5 text-[11px] font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 border-b">
              <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
              Preview — como o cliente vai receber
            </div>
            <div className="bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2260%22 height=%2260%22><circle cx=%2230%22 cy=%2230%22 r=%221.2%22 fill=%22%23cbd5e1%22/></svg>')] bg-slate-50 p-4">
              <div className="max-w-md ml-auto bg-emerald-50 border border-emerald-200 rounded-2xl rounded-tr-sm px-3 py-2 shadow-sm">
                <pre
                  data-testid="whatsapp-preview-text"
                  className="text-sm text-slate-800 whitespace-pre-wrap break-words font-sans leading-relaxed m-0"
                  style={{ fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }}
                >
                  {preview ? renderPreviewWithLinks(preview) : (
                    <span className="text-slate-400 italic">Digite um modelo acima pra ver o preview...</span>
                  )}
                </pre>
                <div className="text-[10px] text-emerald-700/70 text-right mt-1">
                  agora · <CheckCircle2 className="w-2.5 h-2.5 inline text-emerald-500" /><CheckCircle2 className="w-2.5 h-2.5 inline text-emerald-500 -ml-1" />
                </div>
              </div>
              <div className="text-[10px] text-slate-500 text-center mt-2 italic">
                Preview com dados de exemplo · números/links são fake exceto Nome da empresa e WhatsApp da farmácia (se preenchidos acima)
              </div>
            </div>
          </div>

          <div className="border-t pt-3 space-y-2">
            <div className="font-semibold text-sm">Testar Conexão</div>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs">WhatsApp p/ receber teste <span className="text-muted-foreground">(opcional)</span></Label>
                <Input value={zapiTestPhone} onChange={(e) => setZapiTestPhone(e.target.value)} placeholder="(11) 99999-9999" data-testid="input-zapi-test-phone" />
              </div>
              <Button
                onClick={runZapiTest}
                disabled={!f.zapi_configurada || zapiTest?.loading}
                variant="outline"
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                data-testid="btn-test-zapi"
              >
                {zapiTest?.loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Testando…</> : <><MessageCircle className="w-4 h-4 mr-2" />Testar</>}
              </Button>
            </div>
            {!f.zapi_configurada && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="zapi-not-configured-hint">
                Preencha e salve as 3 credenciais acima antes de testar.
              </div>
            )}
            {zapiTest?.result && (
              <div className="p-3 rounded border text-xs space-y-1" data-testid="zapi-test-result">
                <div className="flex items-center gap-2 font-semibold">
                  {zapiTest.result.connection?.ok ? (
                    <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Conectado</>
                  ) : (
                    <><XCircle className="w-4 h-4 text-rose-600" /> Falhou</>
                  )}
                </div>
                {zapiTest.result.connection?.error && (
                  <div className="text-rose-700">Erro: {zapiTest.result.connection.error}</div>
                )}
                {zapiTest.result.connection?.smartphone_connected === false && (
                  <div className="text-amber-700">⚠️ Smartphone desconectado — abra o WhatsApp no celular linkado à instance.</div>
                )}
                {zapiTest.result.send && (
                  <div className={zapiTest.result.send.ok ? "text-emerald-700" : "text-rose-700"}>
                    Mensagem: {zapiTest.result.send.ok ? "✓ enviada" : `✗ ${zapiTest.result.send.error}`}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      )}

      <PartidaCard empresa={f} canEdit={canEdit} raio={f.regiao_raio_centro_km} onRaioChange={(v) => update({ regiao_raio_centro_km: v })}
        onSaved={() => api.get("/empresa").then((r) => setF((prev) => ({ ...prev, partida_endereco: r.data?.partida_endereco, partida_lat: r.data?.partida_lat, partida_lng: r.data?.partida_lng,
          partida_fonte: r.data?.partida_fonte, partida_precisao_m: r.data?.partida_precisao_m })))} />

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Radar className="w-4 h-4"/>Rastreamento em Tempo Real</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {f.rastreamento_gps_liberado === false && (
            <div className="p-3 rounded border border-amber-300 bg-amber-50 text-sm text-amber-900" data-testid="rastreamento-blocked-notice">
              🔒 <b>Bloqueado pela plataforma.</b> O feature de rastreamento GPS em tempo real está desabilitado para sua empresa. Fale com o suporte para liberar essa opção no seu plano.
            </div>
          )}
          <label className={`flex items-start gap-3 p-2 rounded border cursor-pointer ${f.rastreamento_gps_liberado === false ? "bg-slate-50 opacity-60 cursor-not-allowed" : "bg-violet-50/40"}`}>
            <input type="checkbox" checked={!!f.rastreamento_gps_ativo}
              onChange={(e) => update({ rastreamento_gps_ativo: e.target.checked })}
              disabled={!canEdit || f.rastreamento_gps_liberado === false}
              className="mt-1 accent-violet-600" data-testid="chk-rastreamento-gps" />
            <div>
              <div className="font-semibold text-sm">Ativar Rastreamento GPS em Tempo Real</div>
              <div className="text-xs text-muted-foreground">
                Quando <b>ligado</b>: o app do entregador envia posição a cada 30s enquanto tem entrega em <i>Em Trânsito</i>, e o menu <b>Monitoramento em Tempo Real</b> aparece no dashboard.<br />
                Quando <b>desligado</b>: nada de background — o GPS é capturado apenas no momento exato da conclusão da entrega (como antes).
              </div>
            </div>
          </label>
        </CardContent>
      </Card>

      {/* Iter 85 (fev/2026): Backup automático por empresa — até 3 horários por dia */}
      <Card data-testid="card-backup-schedule">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="w-4 h-4 text-sky-600" />
            Backup Automático
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-xs text-muted-foreground bg-slate-50 border rounded p-2 flex gap-2">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
            <div>
              Configure até <b>3 horários</b> por dia para o backup automático da sua empresa rodar (fuso <b>BRT / Brasília</b>).
              O backup gera um ZIP com todas as entregas, entregadores, bairros e configurações — armazenado com segurança no cofre da nuvem.
              Deixe em branco pra usar o horário padrão global (03:00).
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {[1, 2, 3].map((n) => (
              <div key={n}>
                <Label className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-sky-600" />
                  Backup {n} <span className="text-xs text-muted-foreground">(opcional)</span>
                </Label>
                <Input
                  type="time"
                  value={f[`backup_horario_${n}`] || ""}
                  onChange={(e) => update({ [`backup_horario_${n}`]: e.target.value })}
                  disabled={!canEdit}
                  data-testid={`input-backup-horario-${n}`}
                  className="font-mono"
                />
              </div>
            ))}
          </div>
          <div className="text-[11px] text-muted-foreground italic">
            💡 Dica: distribua os horários ao longo do dia (ex: <span className="font-mono">02:00, 12:00, 20:00</span>) pra manter cópias frescas em momentos-chave. Cada backup é independente e pode ser restaurado individualmente na tela <b>Backup</b>.
          </div>
        </CardContent>
      </Card>

      {(canEdit || canEditZapi) && (
        <div className="sticky bottom-4 flex justify-end">
          <Button size="lg" className="bg-orange-600 hover:bg-orange-700 shadow-lg" onClick={save} disabled={saving} data-testid="btn-save-empresa">
            <Save className="w-4 h-4 mr-2" />{saving ? "Salvando..." : "Salvar alterações"}
          </Button>
        </div>
      )}
    </div>
  );
}
